import React, { useContext } from "react";
import styled, { css } from "styled-components";

import { FormContext } from "../../contexts/FormContext";
import { useWrapRef } from "../../hooks/useWrapRef";
import { colors } from "../../themes";
import { colorFromTheme, hsl } from "../../utils/color";
import { inputGlow } from "../../utils/styles";
import { HybridField, useHybridState } from "../../utils/useHybridComponent";
import Button from "../buttons/Button";
import { IconRemoveLine } from "../icons";
import Icon from "../icons/Icon";

export const baseInputStyle = css`
  appearance: none; /** because iOS sucks */
  font-family: inherit;
  font-size: inherit;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.2s;

  &:hover {
    border-color: #999;
  }
`;

const variantInline = css`
  border: none;
  background: none;
  padding: 1rem;
  width: 100%;
  height: 100%;

  :focus {
    background-color: hsla(240, 80%, 97%, 1);
    box-shadow: inset 0 0 0.6rem 0.1rem hsla(240, 80%, 0%, 0.08);
  }

  transition: background-color 0.2s;
`;

const changeValue = (element: HTMLInputElement, value: string) => {
  const setValue = Object.getOwnPropertyDescriptor(
    HTMLInputElement.prototype,
    "value",
  )?.set?.bind(element);

  if (setValue == null) return;

  setValue(value);

  const event = new Event("change", {
    bubbles: true,
    cancelable: false,
  });
  element.dispatchEvent(event);
};

export interface TextInputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  fullWidth?: boolean;
  icon?: typeof Icon;
  label?: string;
  onEnterKeyPress?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  selectOnFocus?: boolean;
  textAlignment?: "left" | "center" | "right" | "justify";
  variant?: "default" | "inline" | "plain";
  showClearButton?: boolean;
  color?: colors;
}

const StyledTextInput = styled.input<TextInputProps>`
  height: 3rem;
  border: solid 1px #ddd;
  padding: 0.5rem;
  border-radius: 0.3rem;
  text-align: ${(props) => props.textAlignment ?? "left"};

  ${(props) => props.disabled && `cursor: not-allowed;`}

  ${({ variant = "default" }) => variant === "inline" && variantInline}
  ${({ variant = "default" }) => variant === "default" && inputGlow}
  ${({ fullWidth }) => fullWidth && `width: 100%;`}
  ${({ icon }) => icon && `padding-left: 2.5rem;`}

  &:invalid {
    border-color: ${(props) => hsl(props.theme?.colors?.danger?.color)};
    --input-glow: ${(props) => hsl(props.theme?.colors?.danger?.color)};
  }

  ${({ color }) =>
    color &&
    css`
      border-color: ${colorFromTheme(color)};
      --input-glow: ${colorFromTheme(color)};
    `}

  ${baseInputStyle}
`;

const LeadingLabel = styled.div`
  line-height: 3rem;
  position: absolute;
  left: 0.5rem;
  color: #888;
`;

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    { disabled, onEnterKeyPress, className, style, showClearButton, ...props },
    ref,
  ) => {
    const form = useContext(FormContext);
    const internalRef = useWrapRef(ref, null);
    const Icon = props.icon ?? (() => <></>);

    const wrapped =
      props.icon != null || props.label != null || showClearButton;

    const Component = (
      <StyledTextInput
        type="text"
        disabled={disabled ?? form.disabled}
        onFocus={() => {
          if (props.selectOnFocus) {
            internalRef.current?.select();
          }
        }}
        onKeyPress={(e) => {
          if (e.key.toLowerCase() === "enter") {
            onEnterKeyPress?.(e);
          }
        }}
        {...{ fullWidth: !!props.icon }}
        {...props}
        className={wrapped ? undefined : className}
        style={wrapped ? undefined : style}
        ref={internalRef}
      />
    );

    if (wrapped) {
      return (
        <label
          style={{ display: "block", position: "relative", ...(style ?? {}) }}
          className={className}
        >
          {props.icon && (
            <Icon
              height="50%"
              style={{
                fill: "#888",
                position: "absolute",
                left: "calc((2.5rem - 16px) / 2) ",
                // middle - half of icon height - 1px for ... baseline reasons?
                top: "calc(50% - 25% - 1px)",
                zIndex: 2,
              }}
            />
          )}
          {props.label != null && <LeadingLabel>{props.label}</LeadingLabel>}
          {Component}
          {showClearButton && (
            <Button
              variant="plain"
              title="Clear"
              onClick={() => {
                if (internalRef.current != null) {
                  changeValue(internalRef.current, "");
                }
              }}
              disabled={disabled}
              style={{
                height: "50%",
                position: "absolute",
                right: "calc((2.5rem - 16px) / 2)",
                top: "calc(50% - 25% - 2px)",
                paddingLeft: "0.2rem",
                paddingRight: "0.2rem",
                backgroundColor: "white",
                zIndex: 1,
              }}
            >
              <IconRemoveLine height="1rem" />
            </Button>
          )}
        </label>
      );
    }

    return Component;
  },
);

const StyledPasswordInputWrapper = styled.div`
  position: relative;
`;

const StyledShowPasswordButton = styled(Button)`
  position: absolute;
  right: 0rem;
  top: 0rem;
  font-size: 1.4rem;
  color: ${colorFromTheme("secondary")};
`;

type PasswordInputProps = Omit<TextInputProps, "type" | "fullWidth"> &
  HybridField<{
    showPassword: boolean;
  }>;

/**
 * TextInput + show/hide password button, which can be either controlled
 * or uncontrolled.
 */
export const PasswordInput = (props: PasswordInputProps) => {
  const [showPassword, onShowPassword] = useHybridState({
    value: props.showPassword,
    onValueChange: props.onShowPasswordChange,
    initialValue: false,
  });

  return (
    <StyledPasswordInputWrapper>
      <TextInput
        {...props}
        type={showPassword ? "text" : "password"}
        fullWidth
      />
      <StyledShowPasswordButton
        variant="plain"
        title="Show password"
        onClick={() => onShowPassword(!showPassword)}
      >
        {showPassword ? "Hide" : "Show"}
      </StyledShowPasswordButton>
    </StyledPasswordInputWrapper>
  );
};

export default TextInput;
